import camelCase from 'lodash/camelCase'
import { Middleware, MiddlewareAPI } from 'redux'

import { makeUpdateTablePagination } from 'actions/ui/shared'
import {
    resetFilters,
    setFilters,
    updateFilter,
    updateFilterSettings,
    updateLocalFilter,
} from 'actions/ui/shared/filter'
import { getCurrentPage, getCurrentTab } from 'routing/pagePaths'
import { CobaltPath } from 'routing/Path'
import { selectDomainValue as selectUiDomainValue } from 'selectors/ui'

/**
 * Filter actions that must snap paginated RQ tables back to page 1. RQ tables
 * read their offset from Redux pagination, so a filter change with a stale
 * offset would request an out-of-range page. Replaces `resetCurrentPaginationSaga`
 * (formerly `takeLatest` on these same actions in `sagas/ui/app`).
 */
const FILTER_ACTION_TYPES = new Set<string>([
    updateFilter.toString(),
    updateLocalFilter.toString(),
    resetFilters.toString(),
    updateFilterSettings.toString(),
    setFilters.toString(),
])

function findTablePaths(
    obj: unknown,
    { maxDepth }: { maxDepth: number }
): string[][] {
    const paths: string[][] = []

    function walk(node: unknown, currentPath: string[]): void {
        if (currentPath.length > maxDepth) {
            return
        }

        if (node && typeof node === 'object') {
            for (const key of Object.keys(node)) {
                if (key === 'table') {
                    paths.push(currentPath)
                } else {
                    walk((node as Record<string, unknown>)[key], [
                        ...currentPath,
                        key,
                    ])
                }
            }
        }
    }

    walk(obj, [])
    return paths
}

function resetPaginationForPath(store: MiddlewareAPI, path: CobaltPath): void {
    const tablePath: CobaltPath = [...path, 'table']
    if (selectUiDomainValue(store.getState(), tablePath)) {
        store.dispatch(makeUpdateTablePagination(tablePath)({ current: 1 }))
    }
}

function resetCurrentPagination(store: MiddlewareAPI): void {
    const { page: pageName } = getCurrentPage()

    if (!pageName) {
        return
    }

    resetPaginationForPath(store, [pageName])

    const tabName = getCurrentTab()
    if (tabName) {
        const tabPath: CobaltPath = [pageName, camelCase(tabName)]
        const val = selectUiDomainValue(store.getState(), tabPath)
        const tableSubPaths = findTablePaths(val, { maxDepth: 2 })
        for (const subPath of tableSubPaths) {
            resetPaginationForPath(store, [...tabPath, ...subPath])
        }
    }
}

export const resetPaginationMiddleware: Middleware =
    (store) => (next) => (action) => {
        const result = next(action)

        if (
            action &&
            typeof action.type === 'string' &&
            FILTER_ACTION_TYPES.has(action.type)
        ) {
            resetCurrentPagination(store)
        }

        return result
    }
